import {useMemo, ReactNode} from 'react';
import {useSelector} from 'react-redux';
import {getMessageJsx} from 'epic-diesel-common';
import {selectMessages} from '../selectors';
import {RootState} from '..';

interface MessageCode {
    code: string;
    args?: (HTMLElement | ReactNode)[];
}

interface Codes {
    [codeKey: string]: MessageCode | string;
}

const transformMessages = <T extends Codes>(i18nMessages, msgCodes: T) => {
    const transformed: Record<keyof T, any> = Object.create({});

    for (const codeKey in msgCodes) {
        if (msgCodes[codeKey]) {
            const msgCode = msgCodes[codeKey];
            if (typeof msgCode === 'object') {
                transformed[codeKey] = getMessageJsx(i18nMessages, msgCode.code, msgCode.args); // array
            } else {
                transformed[codeKey] = getMessageJsx(i18nMessages, msgCode); // string
            }
        }
    }

    return transformed;
};

/**
 * Load i18n messages ONCE and listen for changes, only if provided deps
 * @param msgCodes - i18n codes
 * @param deps - listener to update messages
 */
export const useGetMessages = <T extends Codes>(
    msgCodes: T | (() => T),
    deps: (string | number | boolean)[] = []
) => {
    const i18nMessages = useSelector<RootState, Record<string, string>>(selectMessages);
    return useMemo(() => {
        if (typeof msgCodes === 'function') {
            return transformMessages(i18nMessages, msgCodes());
        }

        return transformMessages(i18nMessages, msgCodes);
    }, deps);
};

export default useGetMessages;
